import { useMutation } from "@apollo/client";
import BookmarkAddOutlinedIcon from "@mui/icons-material/BookmarkAddOutlined";
import React, { ReactElement, useMemo, useState } from "react";

import {
  CreateTemplateInput,
  RenderingObjectKind,
  TemplateRenderingObjectInput,
} from "../../../generated/gql/graphql";
import useLocale from "../../../hooks/use-locale";
import useNotification from "../../../hooks/use-notification";
import SaveAsTemplateModal from "../../../modals/save-as-template-modal";
import { Vector2 } from "../../../types/vector";
import { classNames } from "../../../utils/classes";
import { validateGraphQLResult } from "../../../utils/validate-graphql-result";
import { toTextUpdateInput } from "../../texts/canvas-object-text";

import { CanvasObject } from "./canvas-object";
import { SaveAsTemplateGQL } from "./use-save-as-template";

type SaveAsTemplateButtonProps = {
  layoutName: string;
  backgroundId: string | undefined;
  scale: number;
  offset: Vector2;
  canvasObjects: CanvasObject[];
};

export default function SaveAsTemplateButton(
  props: SaveAsTemplateButtonProps,
): ReactElement {
  const { layoutName, backgroundId, scale, offset, canvasObjects } = props;

  const { getText } = useLocale();
  const notification = useNotification();

  const [createTemplate] = useMutation(SaveAsTemplateGQL, {
    errorPolicy: "all",
  });
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);

  const defaultName = useMemo(
    () => layoutName || getText("sections.imageComposer.saveAsTemplate.button"),
    [layoutName, getText],
  );

  async function handleSubmit(name: string): Promise<void> {
    const renderingObjects: TemplateRenderingObjectInput[] = canvasObjects.map(
      (obj, index) => {
        const input: TemplateRenderingObjectInput = {
          kind: obj.kind,
          scale: obj.scale,
          lockPosition: true,
          lockScale: true,
          offset: obj.offset
            ? {
                x: obj.offset.x,
                y: obj.offset.y,
              }
            : undefined,
          order: index,
        };

        switch (obj.kind) {
          case RenderingObjectKind.Presentation:
            return {
              ...input,
              presentationId: obj.presentationId,
            };
          case RenderingObjectKind.Graphic:
            return {
              ...input,
              graphicId: obj.graphicId,
            };
          case RenderingObjectKind.Text:
            return {
              ...input,
              text: toTextUpdateInput(obj.text),
            };
        }
      },
    );

    const input: CreateTemplateInput = {
      name,
      backgroundId,
      backgroundScale: scale,
      backgroundOffset: {
        x: offset.x,
        y: offset.y,
      },
      renderingObjects,
    };

    const result = await createTemplate({
      variables: {
        input,
      },
    });

    if (
      !validateGraphQLResult(
        result,
        notification,
        getText("notifications.templates.saveAsTemplate.error"),
      )
    ) {
      return;
    }

    notification.addSuccess(
      getText("notifications.templates.saveAsTemplate.success"),
    );
    setIsModalOpen(false);
  }

  return (
    <>
      <button
        type="button"
        className={classNames(
          "flex max-w-[56px] flex-col items-center justify-center rounded-lg bg-white p-2 shadow-lg",
          "text-[10px] leading-tight text-[#999999]",
        )}
        title={getText("sections.imageComposer.saveAsTemplate.tooltip")}
        aria-label={getText("sections.imageComposer.saveAsTemplate.button")}
        onClick={() => setIsModalOpen(true)}
      >
        <BookmarkAddOutlinedIcon className="!text-[#999999]" />
        <span className="mt-1 text-center">
          {getText("sections.imageComposer.saveAsTemplate.button")}
        </span>
      </button>
      <SaveAsTemplateModal
        open={isModalOpen}
        defaultName={defaultName}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleSubmit}
      />
    </>
  );
}
